import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

function formatTime(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
  const seconds = (totalSeconds % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
}

export default function CookingTimer({ isOpen, onClose }) {
  const [minutes, setMinutes] = useState('5');
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [inputError, setInputError] = useState('');
  const deadline = useRef(null);

  useEffect(() => {
    if (!isRunning) return undefined;

    const updateRemaining = () => {
      const next = Math.max(0, Math.ceil((deadline.current - Date.now()) / 1000));
      setRemainingSeconds(next);
      if (next === 0) {
        setIsRunning(false);
        setIsComplete(true);
        if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(200);
      }
    };

    updateRemaining();
    const intervalId = window.setInterval(updateRemaining, 250);
    return () => window.clearInterval(intervalId);
  }, [isRunning]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnEscape = event => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [isOpen, onClose]);

  const startOrResume = () => {
    const duration = remainingSeconds > 0 ? remainingSeconds : Number(minutes) * 60;
    if (!Number.isInteger(duration) || duration < 1 || duration > 24 * 60 * 60) {
      setInputError('Enter a duration from 1 to 1,440 minutes.');
      return;
    }
    setInputError('');
    setIsComplete(false);
    setRemainingSeconds(duration);
    deadline.current = Date.now() + duration * 1000;
    setIsRunning(true);
  };

  const pause = () => {
    if (deadline.current) {
      setRemainingSeconds(Math.max(0, Math.ceil((deadline.current - Date.now()) / 1000)));
    }
    setIsRunning(false);
  };

  const reset = () => {
    setIsRunning(false);
    setIsComplete(false);
    setRemainingSeconds(null);
    setInputError('');
    deadline.current = null;
  };

  if (!isOpen) return null;

  const displayedSeconds = remainingSeconds ?? Math.max(0, Number(minutes) * 60 || 0);

  return createPortal((
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="cooking-timer-title"
        className="w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl"
      >
        <div className="mb-5 flex items-center justify-between">
          <h2 id="cooking-timer-title" className="text-xl font-semibold text-deep-blue">Cooking timer</h2>
          <button type="button" onClick={onClose} className="rounded px-3 py-2 text-sm hover:bg-gray-100" aria-label="Close timer">
            Close
          </button>
        </div>

        <label htmlFor="cooking-timer-minutes" className="mb-2 block text-sm font-medium text-gray-700">
          Duration (minutes)
        </label>
        <input
          id="cooking-timer-minutes"
          type="number"
          min="1"
          max="1440"
          step="1"
          value={minutes}
          disabled={isRunning}
          onChange={event => {
            setMinutes(event.target.value);
            setRemainingSeconds(null);
            setIsComplete(false);
            setInputError('');
          }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-lg disabled:bg-gray-100"
        />

        <div role="timer" aria-live="polite" className="my-6 text-center text-6xl font-bold tabular-nums text-deep-blue">
          {formatTime(displayedSeconds)}
        </div>

        {inputError && <p role="alert" className="mb-4 text-sm text-red-700">{inputError}</p>}
        {isComplete && <p role="status" className="mb-4 text-center font-semibold text-green-700">Time’s up!</p>}

        <div className="flex justify-center gap-3">
          {isRunning ? (
            <button type="button" onClick={pause} className="rounded-lg bg-rich-gold px-5 py-2 font-medium text-white">
              Pause
            </button>
          ) : (
            <button type="button" onClick={startOrResume} className="rounded-lg bg-rich-gold px-5 py-2 font-medium text-white">
              {isComplete ? 'Start again' : remainingSeconds === null ? 'Start' : 'Resume'}
            </button>
          )}
          <button type="button" onClick={reset} className="rounded-lg border border-gray-300 px-5 py-2 font-medium text-gray-700">
            Reset
          </button>
        </div>
      </section>
    </div>
  ), document.body);
}
